import type {
  DeskFile,
  DeskFileAuthor,
  DeskFileCommit,
  DeskFileTree,
  DeskOpenWorkItemFiles,
  DeskWorkItemFiles,
  DeskWorkItemFilesStatus,
  MoveDeskFileRequest,
  PutDeskFileRequest,
  RestoreDeskFileRequest,
  UpdateDeskFileRequest,
} from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, fetchBytes } from '@/lib/api';
import { t } from '@/lib/i18n';
import type { StatusMeta } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

/**
 * A desk's files (docs/features/desk-files.md): a git repository per desk. main is the desk's files, a person's change
 * is a commit on it; a Work Item's runs work in its own copy, merged into main when the item completes.
 */

const enc = encodeURIComponent;

/** A change of main: the new commit, null when nothing changed. */
export interface DeskFileWrite {
  commit: string | null;
  changed: boolean;
}

export const deskFilesApi = {
  tree: (deskId: string, workItemId?: string) => api.request<DeskFileTree>(`/desks/${enc(deskId)}/files`, { query: { workItemId } }),
  /** main's commits, newest first; with a path only those that touched it. */
  history: (deskId: string, query: { path?: string; skip?: number; limit?: number } = {}) =>
    api.request<DeskFileCommit[]>(`/desks/${enc(deskId)}/files/history`, { query }),
  /** The desk's Work Items whose file changes aren't in main yet. */
  openWorkItems: (deskId: string) => api.request<DeskOpenWorkItemFiles[]>(`/desks/${enc(deskId)}/files/work-items`),
  /** baseSha: the content the person started from (DeskFile.sha; null: a new file); another one on main now is a 409. */
  put: (deskId: string, body: PutDeskFileRequest) => api.request<DeskFileWrite & { file: DeskFile | null }>(`/desks/${enc(deskId)}/files`, { method: 'PUT', body }),
  /** A file or a folder with everything in it. */
  remove: (deskId: string, path: string) => api.request<DeskFileWrite>(`/desks/${enc(deskId)}/files`, { method: 'DELETE', query: { path } }),
  /** A file or a whole folder. */
  move: (deskId: string, body: MoveDeskFileRequest) => api.request<DeskFileWrite>(`/desks/${enc(deskId)}/files/move`, { method: 'POST', body }),
  setVisibility: (deskId: string, body: UpdateDeskFileRequest) => api.request<DeskFile>(`/desks/${enc(deskId)}/files`, { method: 'PATCH', body }),
  /** Puts back the content the path had in a commit; before: in the commit before it (a file that commit deleted). */
  restore: (deskId: string, body: RestoreDeskFileRequest) => api.request<DeskFileWrite>(`/desks/${enc(deskId)}/files/restore`, { method: 'POST', body }),
  /** One content by its git id (a diff's sides, a preview, a download). */
  blob: (deskId: string, sha: string) => fetchBytes(`/desks/${enc(deskId)}/files/blobs/${enc(sha)}`),
  /** A path's content in a commit (or a Work Item's copy). */
  contentAt: (deskId: string, path: string, ref: string) => fetchBytes(`/desks/${enc(deskId)}/files/content?path=${enc(path)}&ref=${enc(ref)}`),
  /** null: the Work Item never changed a file. */
  ofWorkItem: async (workItemId: string) => (await api.request<DeskWorkItemFiles | null>(`/work-items/${enc(workItemId)}/files`)) ?? null,
  discard: (workItemId: string) => api.request<DeskWorkItemFiles>(`/work-items/${enc(workItemId)}/files/discard`, { method: 'POST', body: {} }),
};

/** What one JSON upload carries (the Control Plane's body limit); agents' runs upload larger files. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Texts up to this size show and can be edited in the browser. */
export const MAX_TEXT_BYTES = 1024 * 1024;

export const baseName = (path: string) => path.slice(path.lastIndexOf('/') + 1);
/** The folder of a path, '' at the top. */
export const dirName = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');
export const joinPath = (folder: string, name: string) => (folder ? `${folder}/${name}` : name);

/** Cleans a path as the Control Plane keeps it: `/` between folders, no empty names. */
export const cleanPath = (raw: string) =>
  raw
    .replace(/\\/g, '/')
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
    .join('/');

const extension = (path: string) => {
  const name = baseName(path);
  return name.includes('.') ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';
};

// Same as the Control Plane's isTextFile (resources/resource-text.ts).
const TEXT_MIME = /^(text\/|application\/(json|ld\+json|xml|yaml|x-yaml|javascript|sql|x-ndjson|csv))/;
const TEXT_EXTENSIONS = new Set(['txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'yaml', 'yml', 'xml', 'log', 'ini', 'toml', 'sql', 'js', 'ts', 'py', 'sh', 'css', 'html', 'htm']);

/** Plain text by type or name: what shows, can be edited and diffed as text. */
export function isTextFile(path: string, mimeType: string | null | undefined) {
  return TEXT_MIME.test(mimeType ?? '') || TEXT_EXTENSIONS.has(extension(path));
}

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml']);
export const isImageFile = (mimeType: string | null | undefined) => IMAGE_TYPES.has(mimeType ?? '');
export const isPdfFile = (mimeType: string | null | undefined) => mimeType === 'application/pdf';

/** "MD", "PDF" — the extension, else the type. */
export function fileKind(path: string, mimeType: string) {
  return extension(path).toUpperCase() || mimeType;
}

/** Who made a commit: a person's or an agent's name, or Deskworks (a merge). */
export function authorName(a: DeskFileAuthor) {
  if (a.name) return a.name;
  if (a.type === 'agent') return t('an agent');
  if (a.type === 'system') return 'Deskworks';
  return t('someone');
}

/** A commit's short id, as git shows it. */
export const shortSha = (sha: string) => sha.slice(0, 7);

/** The newest of the files' last changes: the desk's last change as far as its files tell. */
export function newestChange(files: DeskFile[]) {
  return files.reduce<DeskFile['lastChange']>((a, f) => (f.lastChange && (!a || f.lastChange.at > a.at) ? f.lastChange : a), null);
}

export type ChangeKind = DeskFileCommit['changes'][number]['status'];
export const CHANGE_KIND: Record<ChangeKind, { label: string; tone: 'green' | 'blue' | 'red' }> = {
  added: { label: t('added'), tone: 'green' },
  changed: { label: t('changed'), tone: 'blue' },
  deleted: { label: t('deleted'), tone: 'red' },
};

/** After this many tries of its agent a conflict waits for a person (the Control Plane's MAX_RESOLVE_ATTEMPTS). */
export const MAX_RESOLVE_ATTEMPTS = 3;

export const WORK_ITEM_FILES_STATUS: Record<DeskWorkItemFilesStatus, StatusMeta> = {
  open: { label: t('Open'), tone: 'blue', hint: t('Its own copy of the desk’s files; goes into the desk when the Work Item is complete.') },
  merging: { label: t('Merging'), tone: 'accent', live: true, hint: t('Going into the desk’s files right now.') },
  conflict: { label: t('Conflict'), tone: 'amber', hint: t('Collides with changes on the desk; its agent resolves them.') },
  merged: { label: t('Merged'), tone: 'green', hint: t('In the desk’s files.') },
  discarded: { label: t('Discarded'), tone: 'muted', hint: t('The changes were dropped.') },
};

// ---------------------------------------------------------------- contents

export async function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error(t('The file could not be read.')));
    reader.readAsDataURL(file);
  });
}

export function textToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** A content as text; null when it is binary (has NUL bytes). */
async function blobText(blob: Blob): Promise<string | null> {
  const text = new TextDecoder('utf-8').decode(await blob.arrayBuffer());
  return text.includes('\u0000') ? null : text.replace(/^﻿/, '');
}

/** A content's text, cached for good (a sha's content never changes). null data: binary. */
export function useDeskBlobText(deskId: string, sha: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.deskBlob(deskId, sha ?? '', 'text'),
    queryFn: async () => blobText(await deskFilesApi.blob(deskId, sha!)),
    enabled: enabled && !!sha,
    staleTime: Infinity,
  });
}

/** A text by its git id, or a path's in a commit (a version's diff, where the history has no blob ids). */
export type DeskTextSource = { sha: string } | { commit: string; path: string };

/** The text of a source, cached for good. null data: binary. */
export function useDeskText(deskId: string, source: DeskTextSource | null) {
  return useQuery({
    queryKey: !source ? ['deskBlob', deskId, null] : 'sha' in source ? qk.deskBlob(deskId, source.sha, 'text') : qk.deskFileAt(deskId, source.commit, source.path),
    queryFn: async () => blobText(await (source && 'sha' in source ? deskFilesApi.blob(deskId, source.sha) : deskFilesApi.contentAt(deskId, source!.path, source!.commit))),
    enabled: !!source,
    staleTime: Infinity,
  });
}

/** A content as a Blob of the given type (an <img> or the browser's PDF viewer); nothing runs on our origin. */
export function useDeskBlob(deskId: string, sha: string | null | undefined, mimeType: string, enabled = true) {
  return useQuery({
    queryKey: qk.deskBlob(deskId, sha ?? '', 'blob'),
    queryFn: async () => new Blob([await deskFilesApi.blob(deskId, sha!)], { type: mimeType }),
    enabled: enabled && !!sha,
    staleTime: Infinity,
  });
}

/** An object URL for a Blob while the component shows it. */
export function useObjectUrl(blob: Blob | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blob]);
  return url;
}

/** Saves a desk file's content (by its sha) under its name. */
export async function downloadDeskFile(deskId: string, sha: string, path: string) {
  const blob = await deskFilesApi.blob(deskId, sha);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = baseName(path);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
